import type { PropertyPrimitiveValue } from "./property-proposal-request";

/**
 * Bridges the existing local D2 draft-staging shape (`DraftEntry` in `draft.ts`, whose `before`/
 * `after` are `unknown` -- `before` is `structuredClone`d straight off the observed IFC property
 * value, any JSON shape; `after` is ALWAYS the raw string a user typed into the value input, even
 * when the property being replaced holds a number or boolean) to the strict, primitive-typed
 * `PropertyPrimitiveValue` the BIM-Hub property-proposal contract requires (§4.5's `PrimitiveValue`
 * union: string/number/boolean only, `before.type` must equal `after.type`).
 *
 * A `DraftEntry` can be staged perfectly validly for local-only editing without ever being eligible
 * for a real BIM-Hub proposal -- `clear`/`remove` operations have no proposal-contract equivalent
 * (v1 is replace-only), and a `before` value that isn't itself a bare string/number/boolean (the
 * `{state:"missing"}` sentinel `draft.ts` uses when a property was absent, or any array/object/null
 * source value) cannot be represented as a `PrimitiveValue` at all. This module's job is to decide
 * that eligibility explicitly, with a human-readable reason, rather than let an ineligible entry
 * either silently fail to submit or throw an unexplained error from deep inside the request builder.
 */

export type DraftEntryProposalEligibility =
  | { readonly eligible: true; readonly before: PropertyPrimitiveValue; readonly after: PropertyPrimitiveValue }
  | { readonly eligible: false; readonly reason: string };

function asPrimitiveValue(value: unknown): PropertyPrimitiveValue | null {
  if (typeof value === "string") return { type: "string", value };
  if (typeof value === "number" && Number.isFinite(value)) return { type: "number", value };
  if (typeof value === "boolean") return { type: "boolean", value };
  return null;
}

/**
 * `rawAfter` is `DraftEntry.after` for a `"replace"` operation, which `draft.ts`'s own
 * `stageDraftChange`/`stageBulkPropertyChange` always set to the plain string typed into the value
 * input -- coerced here to match `beforeType`, the same way a user typing "45" into a field that
 * replaces a `FireRating` *number* means the number 45, not the string "45" (a proposal whose
 * `before`/`after` types disagree is refused by BIM-Hub itself as a 422 `property_proposal_type_
 * mismatch`, so this coercion exists to make the COMMON case -- editing a numeric/boolean property
 * via the same free-text input used for strings -- actually reach BIM-Hub as a same-typed pair,
 * rather than reliably 422ing on every non-string property).
 */
function coerceToMatchingType(rawAfter: unknown, beforeType: PropertyPrimitiveValue["type"]): PropertyPrimitiveValue | null {
  if (typeof rawAfter !== "string") {
    // Not the shape draft.ts actually produces for "replace" (defensive; never hit in practice).
    return asPrimitiveValue(rawAfter);
  }
  if (beforeType === "string") return { type: "string", value: rawAfter };
  if (beforeType === "number") {
    const parsed = Number(rawAfter);
    return rawAfter.trim() !== "" && Number.isFinite(parsed) ? { type: "number", value: parsed } : null;
  }
  // beforeType === "boolean"
  const normalized = rawAfter.trim().toLowerCase();
  if (normalized === "true") return { type: "boolean", value: true };
  if (normalized === "false") return { type: "boolean", value: false };
  return null;
}

export function evaluateDraftEntryForProposal(entry: {
  readonly operation: string;
  readonly before: unknown;
  readonly after: unknown;
}): DraftEntryProposalEligibility {
  if (entry.operation !== "replace") {
    return {
      eligible: false,
      reason: `Property proposals only support "replace" -- this pending change is "${entry.operation}", which has no BIM-Hub property-proposal equivalent (v1).`
    };
  }
  const before = asPrimitiveValue(entry.before);
  if (before === null) {
    return {
      eligible: false,
      reason: "The original value is not a single string/number/boolean, so it cannot be represented as a BIM-Hub property proposal."
    };
  }
  const after = coerceToMatchingType(entry.after, before.type);
  if (after === null) {
    return { eligible: false, reason: `The new value could not be interpreted as a ${before.type} (matching the original value's type).` };
  }
  return { eligible: true, before, after };
}
